import { Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { BehaviorSubject, combineLatest, filter, map, tap } from 'rxjs';

import { UserQuery } from '@common/store/user.store';
import { AuthorisationSnapshot, UserProfile } from '@common/types';
import { isEmpty } from '@common/util/util';

/**
 * AK-708 §6 — roles allowed to EDIT account-wide config in the cascade settings dialog.
 *
 * DELIBERATELY TIGHTER than AK-700's server allow-list, which also permits `enduser`: account
 * config is tenant-wide. Both spellings of the team-member role are accepted for the same reason
 * `isEndUser$` accepts both spellings of end user.
 */
export const ACCOUNT_CONFIG_EDIT_ROLES: readonly string[] = ['admin', 'editor', 'support', 'member', 'team_member'];

/**
 * Whether this user may edit the Account level. Pure and synchronous so callers can evaluate it ONCE
 * (e.g. in `ngOnInit`) instead of per change-detection cycle. `is_support` is honoured the same way
 * `isSupport$` honours it — support is a flag, not only a role.
 */
export function canEditAccountConfig(user: UserProfile | undefined): boolean {
  if (!user) return false;
  if (user.is_support) return true;
  return (user.roles ?? []).some((role) => ACCOUNT_CONFIG_EDIT_ROLES.includes(role));
}

@Injectable({
  providedIn: 'root',
})
export class AuthorisationService {
  constructor(private userQuery: UserQuery) {
    this.user$
      .pipe(
        takeUntilDestroyed(),
        filter((user) => !isEmpty(user)),
        tap((user) => {
          this.snapshot.isAdmin = user.roles?.includes('admin') ?? false;
          this.snapshot.isEditor = user.roles?.includes('editor') ?? false;
          this.snapshot.isSupport = user.is_support ?? false;
          this.snapshot.hasWritePermission = this.snapshot.isAdmin || this.snapshot.isEditor || this.snapshot.isSupport;

          this.snapshotReadySubject.next(this.snapshot);
        }),
      )
      .subscribe();
  }

  snapshot = {
    isAdmin: false,
    isEditor: false,
    isSupport: false,
    hasWritePermission: false,
  };

  snapshotReadySubject = new BehaviorSubject<null | AuthorisationSnapshot>(null);
  snapshotReady$ = this.snapshotReadySubject.asObservable().pipe(filter((snapshot) => snapshot !== null));

  private user$ = this.userQuery.user$;

  isAdmin$ = this.user$.pipe(map((user) => user.roles?.includes('admin')));

  isEditor$ = this.user$.pipe(map((user) => user.roles?.includes('editor')));

  isSupport$ = this.user$.pipe(map((user) => user.is_support || user.roles?.includes('support') || false));

  isGuest$ = this.user$.pipe(map((user) => user.roles?.includes('guest')));

  isEndUser$ = this.user$.pipe(map((user) => user.roles?.includes('enduser') || user.roles?.includes('end_user')));

  hasWritePermission$ = combineLatest([this.isAdmin$, this.isEditor$, this.isSupport$]).pipe(
    map(([isAdmin, isEditor, isSupport]) => isAdmin || isEditor || isSupport),
  );

  /** AK-708 §6 — reactive form of {@link canEditAccountConfig}, for consumers that want a stream. */
  canEditAccountConfig$ = this.user$.pipe(map((user) => canEditAccountConfig(user)));
}
